import { useState } from "react";
import { type API } from "../logic/classApi";
import { createUrl, formatApiDateString, formatApiDatetimeString, isSameDate } from "./apiCommon";
import { showErrorMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";

/** 薬剤納入予定/実績情報APIのURL */
const API_ORDER_PLAN_URL = import.meta.env.VITE_API_ORDER_PLAN_URL;

/** 納入締め日情報APIのURL */
const API_ORDER_EOM_URL = import.meta.env.VITE_API_ORDER_EOM_URL;

/** 薬剤納入予定/実績情報 */
interface OrderData {
  /** 支所コード */
  branchCode: string;
  /** 薬剤ID */
  chemicalId: number;
  /** 納入予定登録日時（ISO日時） */
  orderAdddatetime: string;
  /** 納入予定日時（ISO日時） */
  orderDatetime: string;
  /** 納入完了予定日時（ISO日時） */
  orderDatetimeEnd: string;
  /** 納入予定量 */
  orderVolume: number;
  /** 納入実績日時（ISO日時） */
  orderResultdatetime?: string;
  /** 納入実績量 */
  orderResultvolume?: number;
  /** 非表示操作日時 */
  hiddenAt?: string;
  /** 作成日時 */
  createdAt?: string;
  /** 更新日時 */
  updatedAt?: string;
  /** 削除日時 */
  deletedAt?: string;
}

/** 納入締め日情報 */
interface EomData {
  /** 支所コード */
  branchCode: string;
  /** 締め日年月(YYYYMM) */
  eomYm: string;
  /** 締め日(DD) */
  eomDate: string;
}

/**
 * 薬剤納入予定/実績API
 * @param api APIモジュール
 * @param branchCode 支所コード
 * @returns
 */
export function useOrderApi(api: API, branchCode: string) {
  const [orders, setOrders] = useState<OrderData[]>();
  const [eoms, setEoms] = useState<EomData[]>();

  /**
   * GETリクエスト共通処理
   * @param url リクエストURL
   * @returns APIレスポンス
   */
  async function procGet<T>(url: string): Promise<T> {
    try {
      const result = await api.getRequest<T>(url);
      return result.payload;
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_GET);
    }
  }

  /**
   * POSTリクエスト共通処理
   * @param url リクエストURL
   * @param body リクエストボディ
   */
  async function procPost(url: string, body: any) {
    try {
      await api.postRequestJson(url, body);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_POST);
    }
  }

  /**
   * DELETEリクエスト共通処理
   * @param url リクエストURL
   */
  async function procDelete(url: string) {
    try {
      await api.deleteRequest(url);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_DELETE);
    }
  }

  /**
   * 月の開始日、終了日取得
   * @param yearMonth 年月（yyyy-MM）
   * @returns [月の開始日、終了日]（yyyy-MM-dd）
   */
  const getStartEndDate = (yearMonth: string) => {
    const targetDate = new Date(`${yearMonth}-01`);
    const start = formatApiDateString(targetDate);
    targetDate.setMonth(targetDate.getMonth() + 1);
    targetDate.setDate(targetDate.getDate() - 1);
    const end = formatApiDateString(targetDate);
    return [start, end];
  };

  /**
   * 薬剤納入予定/実績取得（未承認）
   */
  async function loadUnapprovedOrders() {
    const params = { branchCode, getType: 1 };
    const reqUrl = createUrl(API_ORDER_PLAN_URL, params);
    const results = await procGet<OrderData[]>(reqUrl);
    setOrders(results);
  }

  /**
   * 薬剤納入予定/実績取得（納入予定登録日）
   * @param yearmonth 取得対象年月
   */
  async function loadOrdersByRegistrationDate(yearmonth: string) {
    const [startDate, endDate] = getStartEndDate(yearmonth);
    const params = { branchCode, getType: 2, startDate, endDate };
    const reqUrl = createUrl(API_ORDER_PLAN_URL, params);
    const results = await procGet<OrderData[]>(reqUrl);
    setOrders(results);
  }

  /**
   * 薬剤納入予定/実績取得（納入予定日）
   * @param chemicalId 薬剤ID
   * @param startDate 取得開始日
   * @param endDate 取得終了日
   */
  async function loadOrdersByDeliveryDate(chemicalId: number, startDate: Date, endDate: Date) {
    const params = {
      branchCode,
      getType: 3,
      chemicalId,
      startDate: formatApiDateString(startDate),
      endDate: formatApiDateString(endDate),
    };
    const reqUrl = createUrl(API_ORDER_PLAN_URL, params);
    const results = await procGet<OrderData[]>(reqUrl);
    const newOrders = orders?.filter(o => o.chemicalId !== chemicalId) ?? [];
    setOrders([...newOrders, ...results]);
  }

  /**
   * 納入予定登録
   * @param chemicalId 薬剤ID
   * @param date 予定日付
   * @param startTime 予定開始時刻
   * @param endTime 予定終了時刻
   * @param volume 納入量
   */
  async function saveOrder(chemicalId: number, date: string, startTime: string, endTime: string, volume: number) {
    const orderDatetime = formatApiDatetimeString(date, startTime);
    const orderDatetimeEnd = formatApiDatetimeString(date, endTime);
    const body = {
      operationType: "1",
      branchCode,
      chemicalId,
      orderDatetime,
      orderDatetimeEnd,
      orderVolume: volume,
    };
    await api.postRequestJson(API_ORDER_PLAN_URL, body);
  }

  /**
   * 納入予定更新
   * @param chemicalId 薬剤ID
   * @param orderAdddatetime 納入予定登録日時
   * @param date 予定日付
   * @param time 予定開始時刻
   * @param timeEnd 予定終了時刻
   * @param volume 実績納入量
   */
  async function updateOrder(
    chemicalId: number,
    orderAdddatetime: string,
    date: string,
    time: string,
    timeEnd: string,
    volume: number
  ) {
    const orderDatetime = formatApiDatetimeString(date, time);
    const orderDatetimeEnd = formatApiDatetimeString(date, timeEnd);
    const body = {
      operationType: "1",
      branchCode,
      chemicalId,
      orderAdddatetime,
      orderDatetime,
      orderDatetimeEnd,
      orderVolume: volume,
    };
    await procPost(API_ORDER_PLAN_URL, body);
  }

  /**
   * 納入予定削除
   * @param chemicalId 薬剤ID
   * @param orderAdddatetime 納入予定登録日時
   */
  async function deleteOrder(chemicalId: number, orderAdddatetime: string) {
    const params = { branchCode, chemicalId, orderAdddatetime };
    const reqUrl = createUrl(API_ORDER_PLAN_URL, params);
    await procDelete(reqUrl);
  }

  /**
   * 納入実績更新
   * @param chemicalId 薬剤ID
   * @param orderAdddatetime 納入予定登録日時
   * @param date 実績日付
   * @param time 実績時刻
   * @param volume 実績納入量
   */
  async function updateOrderResult(
    chemicalId: number,
    orderAdddatetime: string,
    date: string,
    time: string,
    volume: number
  ) {
    const orderResultdatetime = formatApiDatetimeString(date, time);
    const body = {
      operationType: "2",
      branchCode,
      chemicalId,
      orderAdddatetime,
      orderResultdatetime,
      orderResultvolume: volume,
    };
    await procPost(API_ORDER_PLAN_URL, body);
    await loadUnapprovedOrders();
  }

  /**
   * 納入実績承認
   * @param chemicalId 薬剤ID
   * @param orderAdddatetime 納入予定登録日時
   */
  async function approveOrderResult(chemicalId: number, orderAdddatetime: string) {
    const body = {
      operationType: "3",
      branchCode,
      chemicalId,
      orderAdddatetime,
      hiddenAt: new Date().toISOString(),
    };
    await procPost(API_ORDER_PLAN_URL, body);
    await loadUnapprovedOrders();
  }

  /**
   * 薬剤納入予定/実績情報取得
   * @param chemicalId 薬剤ID
   * @param date 対象日
   * @returns 薬剤納入予定/実績情報
   */
  const getOrdersByDate = (chemicalId: number, date: Date) =>
    orders?.filter(
      o => o.chemicalId === chemicalId && isSameDate(date, o.orderResultdatetime ?? o.orderDatetime)
    ) ?? [];

  /**
   * 納入締め日取得
   * @param startYmd 取得開始日
   * @param endYmd 取得終了日
   */
  async function loadOrderEoms(startYmd: string, endYmd: string) {
    const params = {
      branchCode,
      startYm: startYmd.slice(0, 6),
      endYm: endYmd.slice(0, 6),
    };
    const reqUrl = createUrl(API_ORDER_EOM_URL, params);
    const results = await procGet<EomData[]>(reqUrl);
    const newEoms = eoms?.filter(e => !results.find(r => r.eomYm === e.eomYm)) ?? [];
    setEoms([...newEoms, ...results]);
  }

  /**
   * 納入締め日更新
   * @param eom 締め日リスト
   */
  async function updateEoms(eom: string[]) {
    const body = { branchCode, eom };
    await procPost(API_ORDER_EOM_URL, body);
    await loadOrderEoms(eom[0], eom.slice(-1)[0]);
  }

  return [
    { orders, getOrdersByDate, eoms },
    {
      loadUnapprovedOrders,
      loadOrdersByRegistrationDate,
      loadOrdersByDeliveryDate,
      saveOrder,
      updateOrder,
      deleteOrder,
      updateOrderResult,
      approveOrderResult,
      loadOrderEoms,
      updateEoms,
    },
  ];
}
